import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { combineLatest, distinctUntilChanged, map, Observable, startWith, Subscription, shareReplay } from 'rxjs';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { ConfirmDialogComponent, DEFAULT_SNACKBAR_CONFIG, Logger } from 'src/app/@shared';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { Store, StoreService } from '../..';
import { MatSidenav } from '@angular/material/sidenav';
import { Filter } from 'src/app/@shared/models/filter.model';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StoreDomain } from '../../models/store-domain.model';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

const log = new Logger('StoresComponent');

@Component({
  selector: 'app-stores',
  templateUrl: './stores.component.html',
  styleUrls: ['./stores.component.scss']
})

export class StoresComponent<T extends Store, TStoreDomain extends StoreDomain<Store>> implements OnInit {

  @ViewChild(MatSidenav) sidenav!: MatSidenav;
  @ViewChild('filterSidenav', { read: ElementRef }) filterSidenavElement?: ElementRef<HTMLElement>;
  @ViewChild('filterToggleButton', { read: ElementRef }) filterToggleButton?: ElementRef<HTMLElement>;

  divisions$ = this.storeService.getDivisions().pipe(
    map((res: any) => res.value),
    shareReplay(1)
  );

  viewModel$ = combineLatest([
  this.storeService.stores$,
  this.storeService.isLoading$,
  this.storeService.totalRecords$,
  this.storeService.page$,
  this.storeService.cities$,
  this.storeService.states$,
  this.storeService.filters$,
  this.divisions$
]).pipe(
  map(([stores, isLoading, totalRecords, page, cities, states, filters, divisions]) => {
    if (stores.length === 0 && page.pageIndex > 0) {
      page.previousPageIndex = 0;
      page.pageIndex = 0;
      this.storeService.page(page);
      this.storeService.reload();
    }
    this.cityNames = cities;

    //State filter
    const selectedStateFilters = filters
      .filter(f => f.fieldName === 'states')
      .map(f => f.value);

    //City filter
    const selectedCityFilter = filters.find(f => f.fieldName === 'cities');

    // Divisions filter
    const selectedDivisionFilters = filters
      .filter(f => f.fieldName === 'divisions')
      .map(f => divisions.find((d: any) => d.Id === f.value))
      .filter(d => !!d);

    this.filtersForm.patchValue({
      city: selectedCityFilter ? selectedCityFilter.value : '',
      state: selectedStateFilters.length ? selectedStateFilters : [],
      divisions: selectedDivisionFilters.length ? selectedDivisionFilters : []
    }, { emitEvent: false });

    return { stores, isLoading, totalRecords, page, cities, states, filters, divisions };
  })
);

  filtersForm = new FormGroup({
    search: new FormControl<string | null>(null),
    state: new FormControl<string[]>([]),
    city: new FormControl<string>(''),
    divisions: new FormControl<any[]>([]),
  });

  flexMediaWatcher!: Subscription;
  displayedColumns = ['select', 'StoreName', 'DivisionName', 'Address1', 'City', 'State', 'PostalCode', 'ClientKey', 'Actions'];
  cityNames: string[] = [];
  filteredCityNames$: Observable<string[]> | undefined;
  storeList: TStoreDomain[] = [];
  selection = new SelectionModel<TStoreDomain>(true, []);


  constructor(private storeService: StoreService<T, TStoreDomain>, private mediaObserver: MediaObserver, private dialog: MatDialog,
    private matSnackBar: MatSnackBar) { }

  ngOnInit(): void {
    log.debug('init');
    this.storeService.stores$.subscribe((data) => {
      this.storeList = data;
    })
    this.clearSearch();
    this.storeService.reload();

    this.filtersForm.controls.search.setValue(this.storeService.search);
    const cityControl = this.filtersForm.controls.city;
    this.filteredCityNames$ = cityControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterCityNames(value || ''))
    );
    cityControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(value => {
        if (!value || value.trim() === '') {
          this.storeService.removeFilterByFieldName('cities');
        } else {
          this.updateCityFilter(value);
        }
      });

    // detect changes in viewport size to handle show/hide of table columns
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        ))
      .subscribe((change) => {
        if (change.some(x => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'StoreName', 'Actions'];
        }
        else if (change.some(x => x.mqAlias === 'sm')) {
          this.displayedColumns = ['select', 'StoreName', 'DivisionName', 'ClientKey', 'City', 'State', 'Actions'];
        }
        else {
          this.displayedColumns = ['select', 'StoreName', 'DivisionName', 'ClientKey', 'Address1', 'City', 'State', 'PostalCode', 'Actions'];
        }
      });
  }

  onSearch(event: any) {
    this.storeService.search = event.target.value;
    this.clearSelection();
  }

  clearSearch() {
    this.filtersForm.controls.search.setValue('');
    this.storeService.search = '';
    this.clearSelection();
  }

  onFilterChange(event: any) {
    this.clearSelection();
    switch (event.source.ngControl.name.toLowerCase()) {
      case 'state':
        // get the selected states 
        const statesValues: string[] = event.value;
        //remove any existing states filter  
        this.storeService.removeFilterByFieldName('states');
        // init the array of states filters
        const statesFilters: Filter[] = [];
        // loop through the selected states and add each as a filter
        statesValues.forEach(selection => {
          // init the new filter
          let state = selection ? selection : '';
          const statesFilter: Filter = { displayText: 'State', fieldName: 'states', value: state };
          // add the filter to the list of filters
          statesFilters.push(statesFilter)
        });
        // add the filters to the event service
        this.storeService.addFilters(statesFilters);
        break;
      case 'divisions':
        // get the selected division ids
        const divisionsValues: any[] = event.value;
        // remove any existing division id filters
        this.storeService.removeFilterByFieldName('divisions');
        // init the array of division filters
        const divisionFilters: Filter[] = [];
        // loop through the selected division ids and add each as a filter
        divisionsValues.forEach((value) => {
          // init the new filter
          const divisionFilter: Filter = {
            displayText: value.DivisionName,
            fieldName: 'divisions',
            value: value.Id,
          };
          // add the filter to the list of filters
          divisionFilters.push(divisionFilter);
        });
        // add the filters to the event service
        this.storeService.addFilters(divisionFilters);
        break;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.sidenav?.opened) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (this.filterSidenavElement?.nativeElement.contains(target)) return;
    if (this.filterToggleButton?.nativeElement.contains(target)) return;
    if (target.closest('.cdk-overlay-container')) return;

    this.sidenav.close();
  }

  resetFilters() {
    this.filtersForm.reset();
    this.storeService.search = '';
    this.storeService.clearFilters();
    this.clearSelection();
    this.resetCityFilter();
    this.sidenav.toggle();
  }


  removeFilter(filter: Filter) {
    if ((filter?.fieldName || '').toLowerCase() === 'divisions') {
      this.filtersForm.patchValue({
        divisions: this.filtersForm.controls.divisions.value?.filter(
          (item: any) => item.Id !== filter.value
        )
      });
    }
    this.storeService.removeFilter(filter);
    this.clearSelection();
    if ((filter?.fieldName || '').toLowerCase() === 'cities') {
      this.resetCityFilter();
      this.sidenav.toggle();
    }
  }

   resetCityFilter() {
    this.filteredCityNames$ = this.storeService.cities$.pipe(
      map(cities => [...cities])
    );
  }

  clearFilters() {
    this.filtersForm.reset();
    this.storeService.search = '';
    this.storeService.clearFilters();
    this.clearSelection();
    this.resetCityFilter();
  }

  onSort(sortState: Sort): void {
    this.storeService.sort(sortState);
    this.selection.clear();
  }

  onPage(pageEvent: PageEvent): void {
    this.selection.clear();
    this.storeService.page(pageEvent);
  }

  onCitySelected(event: MatAutocompleteSelectedEvent) {
    const city = event.option.value;
    this.updateCityFilter(city);
  }

  updateCityFilter(city: string) {
    this.clearSelection();
    this.storeService.removeFilterByFieldName('cities');
    const citiesFilter: Filter = { displayText: 'City', fieldName: 'cities', value: city };
    this.storeService.addFilters([citiesFilter]);
  }

  filterCityNames(value: string): string[] {
    const filterValue = value?.toLowerCase();
    return this.cityNames.filter(option => option.toLowerCase().includes(filterValue));
  }


  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.storeList.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.storeList.forEach(row => this.selection.select(row));
  }

  public getSelectedSectionRecords() {
    return this.selection.selected;
  }

  public clearSelection() {
    this.selection.clear();
  }

  deleteSelectedRecords() {
    let selectedRecords: any;
    selectedRecords = this.getSelectedSectionRecords();
    if (selectedRecords && selectedRecords.length > 0) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message: `Do you want to remove Store(s)?`,
        },
        disableClose: true,
      });

      confirmDialog.afterClosed().subscribe(
        confirmResult => {
          if (confirmResult) {
            this.storeService.deleteStores(selectedRecords).subscribe({
              next: () => {
                this.matSnackBar.open(`Store(s) deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
                this.storeService.reload();
                this.clearSelection();
              },
              error: (error) => {
                log.error('Error in deleting Store', error);

                if (error.error.value) {
                  throw new Error(error.error.value);
                } else {
                  throw new Error(error.message);
                }
              }
            });
          }
        });
    }
  }

  downloadJSON() {
    this.storeService.downloadStoresJSON();
  }

  downloadCSV() {
    this.storeService.downloadStoresCSV();
  }
}
